import { css, useTheme } from '@emotion/react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

interface DeleteScriptDialogProps {
  scriptTitle: string;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}

export function DeleteScriptDialog({
  scriptTitle,
  isOpen,
  onConfirm,
  onCancel,
  isDeleting,
}: DeleteScriptDialogProps) {
  const theme = useTheme();

  if (!isOpen) return null;

  return (
    <div
      css={css`
        position: fixed;
        inset: 0;
        background: ${theme.colors.overlay};
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 50;
      `}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-dialog-title"
        css={css`
          background: ${theme.colors.surfaceElevated};
          border: 1px solid ${theme.colors.border};
          border-radius: ${radii.md};
          padding: ${spacing.xl};
          max-width: 400px;
          width: 90%;
          box-shadow: ${shadows.prominent};
        `}
      >
        <h2
          id="delete-dialog-title"
          css={css`
            margin: 0 0 ${spacing.sm};
            font-size: ${fontSizes.xl};
            color: ${theme.colors.text};
          `}
        >
          Delete Script
        </h2>
        <p
          css={css`
            margin: 0 0 ${spacing.xl};
            color: ${theme.colors.textSecondary};
            font-size: ${fontSizes.md};
          `}
        >
          Are you sure you want to delete &ldquo;{scriptTitle}&rdquo;? This action cannot be undone.
        </p>
        <div
          css={css`
            display: flex;
            gap: ${spacing.sm};
            justify-content: flex-end;
          `}
        >
          <Button
            variant="secondary"
            shape="rect"
            size="sm"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            shape="rect"
            size="sm"
            onClick={onConfirm}
            disabled={isDeleting}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.xs};
            `}
          >
            {isDeleting && <Spinner size="sm" aria-hidden />}
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
